import {
  ErrorResponse,
  NotificationsApi,
  ProgramFeatures,
  User,
  WebPushPreload,
} from '@citruscamps/citrus-client'
import { useEffect } from 'react'
import { useQuery } from 'react-query'
import { generateApiConfig } from '../utils/client-config'
import { useRequestHandler } from './useRequestHandler'
import { HttpClient } from '../utils/http'

const fetchToken = async (): Promise<string> => {
  const httpClient = new HttpClient()
  const { token } = await httpClient.get<{ token: string }>('/csrf-token', {
    credentials: 'include',
  })
  return token
}

interface IProps {
  user?: User
  features?: ProgramFeatures
}

interface IWebPushNotifications {
  permission?: NotificationPermission
  subscription?: PushSubscription
  error?: ErrorResponse
  isError: boolean
  isLoading: boolean
  handleTest: () => Promise<void>
}

export const useWebPushNotifications = ({ user, features }: IProps): IWebPushNotifications => {
  const { requestHandler } = useRequestHandler()
  const {
    data: public_key,
    error,
    isLoading,
    isError,
  } = useQuery<string, ErrorResponse>(
    'web_push_public_key',
    async () => {
      const csrfToken = await fetchToken()
      const client = new NotificationsApi(generateApiConfig())
      const { public_key } = await requestHandler<WebPushPreload>(
        () => client.getPreloadWebPush({ X_CSRF_TOKEN: csrfToken }),
        {
          redirect: false,
        },
      )
      return public_key
    },
    {
      enabled: !!user?.id && !!features?.push_notifications_enabled,
    },
  )
  const {
    data: permission,
    error: permissionError,
    isLoading: isLoadingPermission,
    isError: isErrorPermission,
  } = useQuery<NotificationPermission, ErrorResponse>(
    'web_push_permissions',
    async () => {
      if (!('Notification' in window)) {
        throw new Error('This browser does not support desktop notification')
      }
      if (Notification.permission !== 'default') {
        return Notification.permission
      }
      const data = await Notification.requestPermission()
      return data
    },
    {
      enabled: !!user?.id && !!public_key && !!features?.push_notifications_enabled,
    },
  )

  const {
    data: subscription,
    error: subscriptionError,
    isLoading: isLoadingSubscription,
    isError: isErrorSubscription,
  } = useQuery<PushSubscription, ErrorResponse>(
    'web_push_subscription',
    async () => {
      if (!public_key) {
        throw new Error('Public key is required')
      }
      const serviceWorker: ServiceWorkerRegistration = await navigator.serviceWorker.ready
      let subscription: PushSubscription | null = await serviceWorker.pushManager.getSubscription()
      if (subscription === null) {
        subscription = await serviceWorker.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: public_key,
        })
      }
      const csrfToken = await fetchToken()
      const client = new NotificationsApi(generateApiConfig())
      await requestHandler(
        () =>
          client.subscribeWebPush({
            X_CSRF_TOKEN: csrfToken,
            PushSubscription: JSON.parse(JSON.stringify(subscription)),
          }),
        {
          redirect: false,
        },
      )
      return subscription
    },
    {
      enabled:
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        !!public_key &&
        permission === 'granted' &&
        !!features?.push_notifications_enabled,
    },
  )

  const handleTest = async (): Promise<void> => {
    const csrfToken = await fetchToken()
    const client = new NotificationsApi(generateApiConfig())
    await requestHandler(() => client.testWebPush({ X_CSRF_TOKEN: csrfToken }), {
      redirect: false,
    })
  }

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('pushsubscriptionchange', async (event: any) => {
        const oldSubscription: PushSubscription | null = event.oldSubscription
        const newSubscription: PushSubscription | null = event.newSubscription
        const csrfToken = await fetchToken()
        const client = new NotificationsApi(generateApiConfig())
        if (oldSubscription) {
          await requestHandler(
            () =>
              client.unsubscribeWebPush({
                X_CSRF_TOKEN: csrfToken,
                endpoint: oldSubscription.endpoint,
              }),
            {
              redirect: false,
            },
          )
        }
        if (newSubscription) {
          await requestHandler(
            () =>
              client.subscribeWebPush({
                X_CSRF_TOKEN: csrfToken,
                PushSubscription: newSubscription as any,
              }),
            {
              redirect: false,
            },
          )
        }
      })
      return () => navigator.serviceWorker.removeEventListener('pushsubscriptionchange', () => {})
    }
  })

  return {
    handleTest,
    permission,
    subscription,
    error: error || subscriptionError || permissionError || undefined,
    isLoading: isLoading || isLoadingPermission || isLoadingSubscription,
    isError: isError || isErrorPermission || isErrorSubscription,
  }
}
